import { Box, Stack, Typography, Skeleton } from "@mui/material";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { useAppTheme } from "@/hooks/useAppTheme";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { CurrencyFormatterNotationOption, formatUSD } from "@/utils/formatters/currencyFormatter";
import { StatusStat } from "../hooks/useLandingPageData";

interface ClaimsPipelineChartProps {
  data: StatusStat[];
  loading?: boolean;
}

const STATUS_COLORS: Record<string, { light: string; dark: string }> = {
  new: { light: "#6B7280", dark: "#9CA3AF" },
  in_process: { light: "#D97706", dark: "#FBBF24" },
  pended: { light: "#9333EA", dark: "#C084FC" },
  submitted: { light: "#2563EB", dark: "#60A5FA" },
  partially_reimbursed: { light: "#0891B2", dark: "#22D3EE" },
  reimbursed: { light: "#059669", dark: "#34D399" },
  denied: { light: "#DC2626", dark: "#F87171" },
};

const getStatusColor = (status: string, isDark: boolean): string => {
  const colors = STATUS_COLORS[status];
  if (colors) return isDark ? colors.dark : colors.light;
  return isDark ? "#9CA3AF" : "#6B7280";
};

export const ClaimsPipelineChart = ({ data, loading = false }: ClaimsPipelineChartProps) => {
  const { isDarkMode } = useAppTheme();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <Skeleton variant="circular" width={150} height={150} />
      </Box>
    );
  }

  const filteredData = data.filter((d) => d.value.claims > 0);

  if (filteredData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
        <Typography variant="body2" color="text.secondary">
          No claims data
        </Typography>
      </Box>
    );
  }

  const totalClaims = filteredData.reduce((sum, d) => sum + d.value.claims, 0);

  return (
    <Box>
      <ResponsiveContainer width="100%" height={180}>
        <PieChart>
          <Pie
            data={filteredData}
            dataKey="value.claims"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={45}
            outerRadius={70}
            paddingAngle={2}
            strokeWidth={0}
          >
            {filteredData.map((entry) => (
              <Cell key={entry.name} fill={getStatusColor(entry.name, isDarkMode)} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value: number, name: string) => [value, formatToTitleCase(name)]}
            contentStyle={{
              borderRadius: 8,
              border: "1px solid var(--mui-palette-divider)",
              fontSize: 12,
            }}
          />
          {/* Center label */}
          <text
            x="50%"
            y="48%"
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={20}
            fontWeight={700}
            fill="currentColor"
          >
            {totalClaims}
          </text>
          <text x="50%" y="60%" textAnchor="middle" dominantBaseline="middle" fontSize={10} fill="gray">
            total
          </text>
        </PieChart>
      </ResponsiveContainer>

      {/* Legend */}
      <Stack spacing={0.5} sx={{ px: 1 }}>
        {filteredData.map((entry) => (
          <Stack key={entry.name} direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: getStatusColor(entry.name, isDarkMode),
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {formatToTitleCase(entry.name)}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="caption" fontWeight={600}>
                {entry.value.claims}
              </Typography>
              {entry.value.amount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50, textAlign: "right" }}>
                  {formatUSD(entry.value.amount, { notation: CurrencyFormatterNotationOption.COMPACT })}
                </Typography>
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
